/* * * ANGULAR * * */
import { APP_BASE_HREF, CommonModule } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClient } from '@angular/common/http';
import { CUSTOM_ELEMENTS_SCHEMA, NgModule } from '@angular/core';
import { FormsModule, NgForm, ReactiveFormsModule } from '@angular/forms';

import { BrowserModule } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';

/* * * 3RD PARTY * * */
import { TranslateLoader, TranslateModule, TranslateService, TranslateStore } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

/* * * SHARED * * */
import { MultiLocaleModule } from 'multi-locale-input';
import { GlobalNavModule } from 'ngx-global-nav';
import { AnalyticsService, CookieService as NgxCookieService, NOC_ANALYTICS } from 'ngx-shared-services';
// import { TreeSelectorModule } from 'ngx-tree-selector';
import { analytics } from 'noc-analytics';
import { GlobalAlertModule } from './global-alert/global-alert.module';
import { CookieService } from './shared/services/cookie.service';
import { GlobalAlertService } from './shared/services/global-alert.service';
import { NavService } from './shared/services/global-nav.service';
import { I18nService } from './shared/services/i18n.service';

import { AuthHttpInterceptor } from './services/auth.interceptor';
import { MockHttpInterceptor } from './services/mock.interceptor';
import { SubscriptionTableComponent } from './shared/components/subscription-table/subscription-table.component';
import { SelectValidator } from './shared/directives/select-valid.directive';

/* * * COMPONENTS * * */
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { CreateComponent } from './create/create.component';
import { DefaultComponent } from './default/default.component';
import { UserDialogComponent } from './dialogs/user-dialog/user-dialog.component';
import { UpdateStatusDialogComponent } from './dialogs/update_status/update-status-dialog.component';
import { ExitCreateDialogComponent } from './dialogs/exit-create/exit-create-dialog.component';
import { ChangePrimaryOrgDialogComponent } from './dialogs/change-primary-org/change-primary-org-dialog.component';
import { EditComponent } from './edit/edit.component';
import { ListComponent } from './list/list.component';
import { ActiveLocationRolesComponent } from './user-form/active-location-roles/active-location-roles.component';
import { UserStatusBarComponent } from './user-status-bar/user-status-bar.component';
import { ViewComponent } from './view/view.component';

/* * * SERVICES * * */
import { AdminGroupService } from './shared/services/admin-group.service';
import { ApiService } from './shared/services/api.service';
import { ApplicationsService } from './shared/services/applications.service';
import { ApiKeysService } from './shared/services/api-keys.service';
import { BrandsService } from './shared/services/brands.service';
import { BusinessRolesService } from './shared/services/business-roles.service';
import { ContactSubscriptionService } from './shared/services/contact-subscriptions.service';
import { ContactService } from './shared/services/contact.service';
import { DeeplinksService } from './shared/services/deeplinks.service';
import { DrGroupService } from './shared/services/dr-group.service';
import { FormValidatorService } from './shared/services/form-validator.service';
import { LocalesService } from './shared/services/locales.service';
import { MeasurementService } from './shared/services/measurement.service';
import { OrganizationsService } from './shared/services/organizations.service';
import { SidenavService } from './shared/services/sidenav.service';
import { SpaceService } from './shared/services/space.service';
import { SubscriptionTableService } from './shared/services/subscription-table.service';
import { TimezonesService } from './shared/services/timezones.service';
import { UserStatusService } from './shared/services/user-status.service';
import { UserDialogService } from './shared/services/user-dialog.service';
/* * * MATERIAL * * */
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatChipsModule } from '@angular/material/chips';
import { MAT_DATE_FORMATS, MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTreeModule } from '@angular/material/tree';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MatStepperModule } from '@angular/material/stepper';
import { ScrollingModule } from '@angular/cdk/scrolling';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MAT_MOMENT_DATE_FORMATS,
  MatMomentDateModule,
} from '@angular/material-moment-adapter';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatPaginatorModule } from '@angular/material/paginator';
import { TreeModule } from 'enel-tree';
import { NgxGlobalContactFooterModule } from 'ngx-global-contact-footer';
import { SubmitRolesDialogComponent } from './dialogs/submit-roles-dialog/submit-roles-dialog.component';
import { ResourceService } from './services/resource.service';
import { OrganizationPrimaryPipe } from './shared/pipes/organization.pipe';
import { PhoneNumberPipe } from './shared/pipes/phone.number.pipe';
import { TranslateObjectPipe } from './shared/pipes/translate.obj.pipe';
import { UserNamePipe } from './shared/pipes/username.pipe';
import { CountriesService } from './shared/services/countries.service';
import { ItsService } from './shared/services/its.service';
import { PermissionService } from './shared/services/permissions.service';
import { UsertypeService } from './shared/services/usertype.service';
import { ActiveLocationComponent } from './user-form/active-location-roles/active-location/active-location.component';
import { RoleChipComponent } from './user-form/active-location-roles/role-chip/role-chip.component';
import { LocationRolesComponent } from './user-form/location-roles/location-roles.component';
import { UserFormComponent } from './user-form/user-form.component';
import { NgxDeeplinkerModule } from 'ngx-deeplinker';
import { StepperIconComponent } from './shared/components/stepper-icon/stepper-icon.component';
import { MixPanelService } from './shared/services/mixpanel.service';

export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, '/assets/i18n/', '.json');
}

export function getBaseLocation() {
  const cookies = {};
  document.cookie.split(';').map((variable) => {
    const variableParts = variable.split('=');
    cookies[variableParts[0].trim()] = variableParts[1];
  });
  const urlParts = location.pathname.split('?');
  const paths = urlParts[0].split('/');
  const pathBrand = paths[1];
  const defaultBrand = 'enelx';
  const brand = (cookies['default_brand'] || '').toLowerCase();

  if (brand && pathBrand !== brand && brand !== defaultBrand) {
    return `/${brand}`;
  } else if (pathBrand === brand) {
    return `/${brand}`;
  } else {
    return `/`;
  }
}
@NgModule({
  declarations: [
    AppComponent,
    ListComponent,
    CreateComponent,
    EditComponent,
    UserFormComponent,
    ViewComponent,
    DefaultComponent,
    ActiveLocationRolesComponent,
    SelectValidator,
    PhoneNumberPipe,
    UserNamePipe,
    OrganizationPrimaryPipe,
    TranslateObjectPipe,
    LocationRolesComponent,
    RoleChipComponent,
    ActiveLocationComponent,
    RoleChipComponent,
    UpdateStatusDialogComponent,
    UserStatusBarComponent,
    UserDialogComponent,
    SubscriptionTableComponent,
    SubmitRolesDialogComponent,
    StepperIconComponent,
    ExitCreateDialogComponent,
    ChangePrimaryOrgDialogComponent,
  ],
  imports: [
    BrowserModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    GlobalNavModule.forRoot({ namespace: 'ngx-global-nav.' }),
    NgxGlobalContactFooterModule.forRoot({ namespace: 'ngx-global-contact-footer.' }),
    GlobalAlertModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    }),
    RouterModule.forRoot([], {}),
    AppRoutingModule,
    MatFormFieldModule,
    MatSelectModule,
    MatTabsModule,
    MatTableModule,
    MatTreeModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatDatepickerModule,
    MatMomentDateModule,
    MatIconModule,
    MatCheckboxModule,
    MatButtonModule,
    MatDialogModule,
    MatSidenavModule,
    MatAutocompleteModule,
    MatExpansionModule,
    ReactiveFormsModule,
    MatListModule,
    MatMenuModule,
    MatSlideToggleModule,
    MatTooltipModule,
    MultiLocaleModule,
    ScrollingModule,
    MatRadioModule,
    MatPaginatorModule,
    MatToolbarModule,
    MatChipsModule,
    MatOptionModule,
    TreeModule,
    NgxDeeplinkerModule,
    MatStepperModule,
  ],
  providers: [
    TranslateService,
    AnalyticsService,
    NgxCookieService,
    CookieService,
    { provide: HTTP_INTERCEPTORS, useClass: MockHttpInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    HttpClient,
    ApiService,
    GlobalAlertService,
    I18nService,
    UserStatusService,
    BrandsService,
    MeasurementService,
    BusinessRolesService,
    ApplicationsService,
    TimezonesService,
    LocalesService,
    UsertypeService,
    CountriesService,
    DeeplinksService,
    SpaceService,
    SubscriptionTableService,
    AdminGroupService,
    MatDatepickerModule,
    SidenavService,
    NavService,
    OrganizationsService,
    ContactSubscriptionService,
    ContactService,
    UserDialogService,
    ItsService,
    DrGroupService,
    FormValidatorService,
    NgForm,
    PermissionService,
    ResourceService,
    MixPanelService,
    ApiKeysService,
    {
      provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS,
      useValue: {
        useUtc: true,
        strict: true,
      },
    },
    { provide: MAT_DATE_FORMATS, useValue: MAT_MOMENT_DATE_FORMATS },
    { provide: NOC_ANALYTICS, useValue: analytics },
    TranslateStore,
    {
      provide: APP_BASE_HREF,
      useFactory: getBaseLocation,
    },
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
